import { FileText, Receipt, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useBodyLock } from "../../hooks/useBodyLock";
import { apiDownload, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";

interface OrderDocumentsModalProps {
  order: { id: string; orderNo: string } | null;
  documentsOn: boolean;
  invoicingOn: boolean;
  onClose: () => void;
}

export function OrderDocumentsModal({
  order,
  documentsOn,
  invoicingOn,
  onClose,
}: OrderDocumentsModalProps) {
  useBodyLock(!!order);

  async function download(type: "RECEIPT" | "INVOICE") {
    if (!order) return;
    const filename =
      type === "INVOICE" ? `invoice-${order.orderNo}.pdf` : `receipt-${order.orderNo}.pdf`;
    try {
      await apiDownload(`/orders/mine/${order.id}/documents/${type}`, filename);
      onClose();
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Download failed");
    }
  }

  const options = [
    documentsOn
      ? {
          type: "RECEIPT" as const,
          label: "Payment receipt",
          hint: "PDF receipt for this order",
          icon: Receipt,
        }
      : null,
    invoicingOn
      ? {
          type: "INVOICE" as const,
          label: "Tax invoice",
          hint: "Official tax invoice (e-invoice)",
          icon: FileText,
        }
      : null,
  ].filter(Boolean) as {
    type: "RECEIPT" | "INVOICE";
    label: string;
    hint: string;
    icon: typeof Receipt;
  }[];

  return (
    <AnimatePresence>
      {order && options.length > 0 && (
        <motion.div
          key="order-documents-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", damping: 28, stiffness: 360 }}
            className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-surface p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-documents-title"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <h2 id="order-documents-title" className="pr-8 text-center font-display text-sm font-semibold text-white">
              How would you like to get your receipt?
            </h2>

            <div className="mt-4 space-y-2.5">
              {options.map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => void download(opt.type)}
                  className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3.5 text-left transition-colors hover:border-brand-pink/30 hover:bg-brand-pink/5 active:scale-[0.99]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-pink/10 text-brand-pink">
                    <opt.icon size={20} strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-white">{opt.label}</span>
                    <span className="mt-0.5 block text-[11px] text-white/45">{opt.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
